import React, { useEffect, useRef, useState } from 'react';
import { Dropdown, Flex, MenuProps, notification, Space, Tooltip } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { faAngleDown, faArrowRightFromFile } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DateTime } from 'luxon';
import { useParams } from 'react-router-dom';

import { trackEvent } from 'jf/analytics/Analytics';
import { SlackUser, StudyClient } from 'jf/api';
import { useDevExTheme } from 'jf/common/themes/DevExTheme';
import { DevExButton } from 'jf/components/DevExButton';
import { DevExCheckbox } from 'jf/components/DevExCheckbox';
import { exportCSV } from 'jf/pages/studyAnalyzer/export/ExportStudyModal';
import { useLocalStorage } from 'jf/utils/useBrowserStorage';
import { useClientQuery } from 'jf/utils/useClientQuery';
import { useLivePropState } from 'jf/utils/useLivePropState';

import { useStudyBuilderState } from '../StudyBuilderState';

import { RosterTargetDrawer } from './RosterTargetDrawer';
import { SlackTargetDrawer } from './SlackTargetDrawer';
import { SlackTargetList } from './SlackTargetList';
import { useStudySlackTargetUpdater } from './useStudySlackTargetUpdater';

export const SlackTargetEditor: React.FC = (props) => {
  const theme = useDevExTheme();
  const state = useStudyBuilderState();
  const initialSlackUsersSet = useRef(false);
  const [slackUsers, setSlackUsers] = useLivePropState(state.slackUsers, (slackUsers) =>
    state.update({ slackUsers })
  );

  const [slackDrawerOpen, setSlackDrawerOpen] = useState(false);
  const [showWarning, setShowWarning] = useLocalStorage('showWarning', false);

  const { studyRef: studyRefParam } = useParams<{ studyRef: string }>();
  const { update: saveSlackTargets, isUpdating: slackTargetsUpdating } =
    useStudySlackTargetUpdater(studyRefParam);
  const { data: study } = useClientQuery(StudyClient.getStudy, { studyRef: studyRefParam });
  const [duplicates, setDuplicates] = useState(0);

  const [rosterDrawerOpen, setRosterDrawerOpen] = useState(false);

  useEffect(() => {
    if (state.slackUsersLoaded) {
      if (initialSlackUsersSet.current) {
        // save slack targets on change, but not on first render
        saveSlackTargets(slackUsers);
      } else {
        initialSlackUsersSet.current = true;
      }
    }
  }, [slackUsers]);

  const exportToCSV = (targets: SlackUser[]) => {
    if (!targets || targets.length === 0) {
      return;
    }
    const headerLabels = ['Name', 'Email', 'Username'];
    const selectedKeys = ['realName', 'email', 'userName'];
    const headers = headerLabels.join(',');
    const csvData = [[headers]];

    for (const target of targets) {
      const dataRow = [selectedKeys.map((h) => target[h]).join(',')];
      csvData.push(dataRow);
    }
    const fileNameParts = [
      study!.name,
      'Respondents',
      DateTime.now().toLocaleString(DateTime.DATE_SHORT).replaceAll('/', '-'),
    ];
    exportCSV(csvData, fileNameParts);
  };
  const [api, contextHolder] = notification.useNotification();

  const openNotification = () => {
    !showWarning &&
      !slackDrawerOpen &&
      api.info({
        message: (
          <Flex vertical gap={theme.variable.spacing.md}>
            {duplicates} {duplicates === 1 ? 'person was' : 'people were'} not added because they
            were already included in your list
            <Flex gap={theme.variable.spacing.sm}>
              <DevExCheckbox onChange={() => setShowWarning(!showWarning)} /> Don't warn me again
            </Flex>
          </Flex>
        ),
        placement: 'topRight',
        duration: 0,
        onClose: () => notification.destroy(),
      });
  };

  useEffect(() => {
    if (duplicates > 0) {
      openNotification();
    }
    setDuplicates(0);
  }, [duplicates, slackUsers]);

  const items: MenuProps['items'] = [
    {
      label: 'Add people from your Jellyfish roster',
      key: '1',
    },
    {
      label: 'Add Slack users',
      key: '2',
    },
  ];

  const handleMenuClick: MenuProps['onClick'] = (e) => {
    if (e.key === '1') {
      setRosterDrawerOpen(true);
      trackEvent('survey-editor:slack-target-editor:add-roster');
    } else {
      setSlackDrawerOpen(true);
      trackEvent('survey-editor:slack-target-editor:add-slack');
    }
  };

  const menuProps = {
    items,
    onClick: handleMenuClick,
  };

  return (
    <Flex vertical gap={theme.variable.spacing.md}>
      {contextHolder}
      <Flex vertical align="center" gap={theme.variable.spacing.sm}>
        <SlackTargetList
          users={slackUsers}
          onUpdate={setSlackUsers}
          loading={!state.slackUsersLoaded}
          saving={slackTargetsUpdating}
          loadingText="Loading Slack targets..."
          usersLabel={
            <strong>
              {slackUsers.length} respondent{slackUsers.length === 1 ? '' : 's'}
            </strong>
          }
          trackEventKey="slack-target-editor"
          extra={[
            <Tooltip
              key="export-csv"
              title={
                slackUsers.length === 0
                  ? 'No respondents to export'
                  : 'Export the list of respondents to a CSV'
              }
            >
              <DevExButton
                size="default"
                type="outline"
                icon={<FontAwesomeIcon icon={faArrowRightFromFile} />}
                onClick={() => {
                  trackEvent('survey-editor:slack-target-editor:export-csv');
                  exportToCSV(slackUsers);
                }}
                disabled={slackUsers.length === 0}
              />
            </Tooltip>,
            <Dropdown key="open-drawer" menu={menuProps}>
              <DevExButton type="outline">
                <Space>
                  Add
                  <FontAwesomeIcon icon={faAngleDown} color={theme.color.text.secondary} />
                </Space>
              </DevExButton>
            </Dropdown>,
          ]}
          emptyStateCTA={
            <Dropdown menu={menuProps}>
              <DevExButton type="outline">
                <Space>
                  Add
                  <DownOutlined />
                </Space>
              </DevExButton>
            </Dropdown>
          }
          emptyText="Start adding Slack users by selecting them from your Jellyfish roster or directly from Slack"
        />
      </Flex>
      <RosterTargetDrawer
        onClose={() => setRosterDrawerOpen(false)}
        open={rosterDrawerOpen}
        addUsers={(users) => {
          setSlackUsers((oldUsers) => {
            const typecastUsers = users.map((u) => u.slack!).filter((u) => !!u);
            return [
              ...oldUsers,
              // filter out duplicates before adding
              ...typecastUsers.filter(
                (newTarget) =>
                  !oldUsers.some((target) => target.slackUserId === newTarget.slackUserId)
              ),
            ];
          });
        }}
        slackUsers={slackUsers}
      />
      <SlackTargetDrawer
        onClose={() => setSlackDrawerOpen(false)}
        open={slackDrawerOpen}
        onDuplicateChange={setDuplicates}
        slackUsers={slackUsers}
        addUsers={(users) => {
          setSlackUsers((oldUsers) => {
            return [...oldUsers, ...users]; // pseudocode
          });
        }}
      />
    </Flex>
  );
};
